import { ICTCAccountService } from "../interfaces/ICTCAccountsService";
import { IOrganization } from "../interfaces/IOrganization";
import { httpClient } from "../HttpClient";
import { BaseService } from "../BaseService";
import { AxiosRequestConfig } from "axios";
import { IDomain } from "../interfaces/IDomain";
import CreateDomainRequest from "../request_objects/accounts/organizations/CreateDomainRequest";
import Domain from "../Domain";
import Organization from "../Organization";
import { IAccessKey } from "../interfaces/IAccessKey";

export class OrganizationService implements BaseService, ICTCAccountService {
  public END_POINT: string = "accounts";
  public VERSION: string = process.env.VUE_APP_ACCOUNTS_VERSION;
  public ORGANIZATIONS: string = `${this.END_POINT}/${this.VERSION}/organizations`;
  public NAME: string = "name";
  public DOMAINS: string = "domains";
  public CMS_END_POINT: string = "cms";
  public CSL_END_POINT: string = "csl";
  public CHARTS_END_POINT: string = "charts/v1";

  public CMS_ORGANIZATIONS: string = `${this.CMS_END_POINT}/${this.VERSION}/organizations`;

  public async getOrganizations(): Promise<IOrganization> {
    return await httpClient.get(`${this.ORGANIZATIONS}`, {
      params: {
        pageSize: 10000,
      },
    });
  }

  public async createOrganization(
    organization: Organization
  ): Promise<IOrganization> {
    return await httpClient.post(`${this.ORGANIZATIONS}`, organization);
  }

  public async updateOrganizationStatus(
    organization: Organization
  ): Promise<IOrganization> {
    return await httpClient.patch(
      `${this.ORGANIZATIONS}/${organization.id}/status`,
      { status: organization.status.intValue }
    );
  }

  public async updateOrganization(
    organization: Organization
  ): Promise<IOrganization> {
    return await httpClient.put(
      `${this.ORGANIZATIONS}/${organization.id}`,
      organization
    );
  }

  public async isOrganizationExists(
    organization: Organization
  ): Promise<IOrganization> {
    return await httpClient.head(`${this.ORGANIZATIONS}/${this.NAME}`, {
      params: {
        name: organization.name,
      },
    });
  }

  public async getDomains(organization: Organization): Promise<Array<IDomain>> {
    return await httpClient.get<Array<IDomain>>(
      `${this.ORGANIZATIONS}/${organization.id}/${this.DOMAINS}`
    );
  }

  public async createDomain(
    id: string,
    config: CreateDomainRequest
  ): Promise<IDomain> {
    return await httpClient.post<IDomain>(
      `${this.ORGANIZATIONS}/${id}/${this.DOMAINS}`,
      config
    );
  }

  public async removeDomain(domain: Domain): Promise<IDomain> {
    return await httpClient.delete<IDomain>(
      `${this.ORGANIZATIONS}/${domain.organizationId}/${this.DOMAINS}/${domain.id}`
    );
  }

  public async getAccessKeys(
    organizationId: string
  ): Promise<Array<IAccessKey>> {
    return await httpClient.get<Array<IAccessKey>>(
      `${this.ORGANIZATIONS}/${organizationId}/access-keys`
    );
  }
  public async generateAccessKey(
    organizationId: string,
    accessKey: IAccessKey
  ): Promise<string> {
    return await httpClient.put<string>(
      `${this.ORGANIZATIONS}/${organizationId}/rotate-access-key?keyType=${accessKey.keyType}&accessKey=${accessKey.accessKey}&expirationDate=${accessKey.expirationDate}`
    );
  }
  public async updateAccessKey(
    organizationId: string,
    keyType: string,
    expirationDate: string
  ): Promise<string> {
    return await httpClient.patch<string>(
      `${this.ORGANIZATIONS}/${organizationId}/update-access-key?organizationId=${organizationId}&keyType=${keyType}&expirationDate=${expirationDate}`
    );
  }
  public async uploadOrgImage(
    organizationId: string,
    image: FormData
  ): Promise<string> {
    return await httpClient.put<string>(
      `${this.ORGANIZATIONS}/${organizationId}/image`,
      image
    );
  }

  public async getOrganizationSettings(
    organizationId: string
  ): Promise<object> {
    return await httpClient.get<object>(
      `${this.CMS_ORGANIZATIONS}/${organizationId}/settings`
    );
  }

  public async createCMSOrganization(organizationId: string): Promise<object> {
    return await httpClient.post<object>(
      `${this.CMS_ORGANIZATIONS}/${organizationId}`
    );
  }

  public async updateOrganizationSettings(
    organizationId: string,
    settings: object
  ): Promise<string> {
    return await httpClient.put<string>(
      `${this.CMS_ORGANIZATIONS}/${organizationId}/settings`,
      settings
    );
  }

  public async generateAutoRenewalReport(): Promise<string> {
    return await httpClient.get<string>(
      `${this.CSL_END_POINT}/auto-renewal-report/${this.VERSION}/auto-renewal-report`
    );
  }

  public async generateConcurrencyLicenseUsageReport(
    organizationId: string,
    days: number,
    productIds: []
  ) {
    return await httpClient.post(
      `${this.CHARTS_END_POINT}/csl/concurrent-license-usage-report?organizationId=${organizationId}&days=${days}`,
      productIds
    );
  }
  
}
